/**
 * Copyright Highway9 Networks Inc.
 *
 * Single concurrent execution per key (shared promise). Used by `fetchInFlightDedupe` and `apiFetch`.
 */

/** `key === null` skips deduplication and always runs `factory`. */
/**
 * Executes the provided factory and deduplicates concurrent executions per `key`.
 * If a call is already in flight for the same key, returns the shared promise.
 *
 * @param key - Unique identifier for deduplication. If `null`, disables deduplication.
 * @param map - Map tracking active promises by key.
 * @param factory - Function returning the promise to execute.
 * @returns Promise<T> - The ongoing or newly created promise.
 */
export async function dedupePromise<T>(
  key: string | null,
  map: Map<string, Promise<T>>,
  factory: () => Promise<T>
): Promise<T> {
  // If key is null, skip deduplication and execute factory directly.
  if (key === null) {
    return factory();
  }
  
  // Check if a promise is already in flight for this key.
  let pending = map.get(key);
  if (!pending) {
    // If not, invoke the factory and ensure the promise is removed from the map when settled.
    pending = factory().finally(() => map.delete(key));
    map.set(key, pending);
  }
  
  // Return the ongoing or newly created promise.
  return pending;
}
